import { useTranslation } from "react-i18next";
import { KeyRound } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@nexus/ui";
import { GoogleIcon } from "@/components/icons/GoogleIcon";

interface AuthProviderBadgeProps {
  provider: string | null;
  /** "cell" — plain icon + label for table rows. "pill" — tooltipped rounded pill for drawer headers. */
  variant?: "cell" | "pill";
}

/**
 * Renders which auth provider a user signs in with. Single source for what
 * used to be a `AuthProviderCell` copy in the People table and an
 * `AuthProviderBadge` copy in the Person drawer.
 */
export function AuthProviderBadge({ provider, variant = "pill" }: AuthProviderBadgeProps) {
  const { t } = useTranslation();

  if (!provider) {
    return variant === "cell" ? <span className="text-xs text-muted-foreground">—</span> : null;
  }

  const isGoogle = provider === "google.com";

  if (variant === "cell") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-foreground">
        {isGoogle ? (
          <GoogleIcon className="h-3.5 w-3.5 shrink-0" />
        ) : (
          <KeyRound className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        )}
        {isGoogle ? t("iam.authProviderBadge.google") : t("iam.authProviderBadge.password")}
      </span>
    );
  }

  const badge = isGoogle ? (
    <span className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-400">
      <GoogleIcon className="h-2.5 w-2.5 shrink-0" />
      {t("iam.authProviderBadge.google")}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full border border-border px-1.5 py-0.5 text-[10px] font-medium bg-muted text-muted-foreground">
      <KeyRound className="h-2.5 w-2.5 shrink-0" />
      {t("iam.authProviderBadge.password")}
    </span>
  );

  const tooltip = isGoogle
    ? t("iam.personDrawer.authProvider.google", "Signs in with Google")
    : t("iam.personDrawer.authProvider.password", "Signs in with email and password");

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" className="inline-flex shrink-0">
            {badge}
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom">{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
